/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Notification component
 *
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @version 1.17.0
 * @since 1.0.0
 */

import React, { PureComponent } from 'react';

import debounce from 'lodash/debounce';
import moment, { Moment } from 'moment';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { DEBOUNCE_DEFAULT_DELAY } from '@modules/app/appConstants';
import {
    makeSelectNotificationLastUserActionInterval,
    makeSelectNotificationNotifierInterval,
} from '@pages/PageAuth/selectors/authSelectors';
import { IRootReducer } from '@reducers/rootReducer';

import { getNotificationsStart } from '../notificationActions';

interface IDispatchProps {
    getNotificationsStart: () => void;
}

interface IConnectedProps {
    notifierInterval: number;
    lastUserActionInterval: number;
}

type NotificationAppProps = IConnectedProps & IDispatchProps;

export class Notification extends PureComponent<NotificationAppProps> {
    lastActivity: Moment = moment();
    mouseClickHandler = debounce(() => {
        this.lastActivity = moment();
    }, DEBOUNCE_DEFAULT_DELAY);
    private timerId: number | undefined;

    componentDidMount(): void {
        this.loadNotification();
        window.addEventListener('click', this.mouseClickHandler, true);
    }

    componentDidUpdate() {
        if (this.props.notifierInterval > 0) {
            this.timerId = window.setInterval(this.loadNotification, this.props.notifierInterval);
        }
    }

    componentWillUnmount(): void {
        clearInterval(this.timerId);
        window.removeEventListener('click', this.mouseClickHandler, true);
    }

    loadNotification = () => {
        const interval = moment().diff(this.lastActivity, 'milliseconds');
        if (interval < this.props.lastUserActionInterval) {
            this.props.getNotificationsStart();
        }
    };

    render() {
        return null;
    }
}

const mapConnectToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    notifierInterval: makeSelectNotificationNotifierInterval(),
    lastUserActionInterval: makeSelectNotificationLastUserActionInterval(),
});

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    getNotificationsStart: () => dispatch(getNotificationsStart()),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default connect(mapConnectToProps, mapDispatchToProps)(Notification as any) as unknown as React.FC<any>;
